import { OptionsType } from '@openx/types/options';
import type { PackageProviderConfig } from '@openx/types/package/packageProviderConfig';
import { noop } from '@openx/utils/noop';
import { useDeviceTypesOptionsFetch } from '../targeting/utils/useDeviceTypesOptionsFetch';
import { useAccountOptionsFetch } from './utils/useAccountOptionsFetch';
import { useCloneForm } from './utils/useCloneForm';
import { useCreateForm } from './utils/useCreateForm';
import { useDealDisableRbo } from './utils/useDealDisableRbo';
import { useDealsFetch } from './utils/useDealsFetch';
import { useDetailsConfig } from './utils/useDetailsConfig';
import { useDispatchGetNetworkAndPublishersAccountsOptions } from './utils/useDispatchGetNetworkAndPublishersAccountsOptions';
import { useDistributionChannelOptionsFetch } from './utils/useDistributionChannelOptions';
import { useOldDistributionChannelOptionsFetch } from './utils/useOldDistributionChannelOptions';
import { usePackageClone } from './utils/usePackageClone';
import { usePackageCreate } from './utils/usePackageCreate';
import { usePackageFetch, usePackageLazyFetch } from './utils/usePackageFetch';
import { usePackageOperationActions } from './utils/usePackageOperationActions';
import { usePackagePermissions } from './utils/usePackagePermissions';
import { usePackageRetargetingDealsValidation } from './utils/usePackageRetargetingDealsValidation';
import { usePackagesListFetch } from './utils/usePackagesListFetch';
import { usePackageUpdate } from './utils/usePackageUpdate';
import { usePackageUsedBy } from './utils/usePackageUsedBy';
import { useRelatedAccountsFetch } from './utils/useRelatedAccountsFetch';
import { useTargetablePackagesFetch } from './utils/useTargetablePackagesFetch';
import { useTechnologyOptionsFetch } from './utils/useTechnologyOptionsFetch';
import { useUpdateForm } from './utils/useUpdateForm';

const todoConfig = {
  useFloorsFetch: () => {
    throw new Error('useFloorsFetch: Not implemented. Ensure you are inside the PackageContext');
  },
};

export const packageConfig: PackageProviderConfig = {
  ...todoConfig,
  isOa: true,
  isOaSuborg: false,
  useAccountOptionsFetch,
  useCloneForm,
  useCreateForm,
  useDealsFetch,
  useDetailsConfig,
  useDispatchGetNetworkAndPublishersAccountsOptions,
  useDeviceTypesOptionsFetch: () => {
    const deviceTypesOptions = useDeviceTypesOptionsFetch();

    return {
      [OptionsType.DESKTOP_DEVICES]: {
        data: deviceTypesOptions[OptionsType.DESKTOP_DEVICES].mapById,
        isLoading: deviceTypesOptions[OptionsType.DESKTOP_DEVICES].loading,
        refetch: deviceTypesOptions[OptionsType.DESKTOP_DEVICES].refetch || (() => Promise.resolve()),
      },
      [OptionsType.MOBILE_DEVICES]: {
        data: deviceTypesOptions[OptionsType.MOBILE_DEVICES].mapById,
        isLoading: deviceTypesOptions[OptionsType.MOBILE_DEVICES].loading,
        refetch: deviceTypesOptions[OptionsType.MOBILE_DEVICES].refetch || (() => Promise.resolve()),
      },
      [OptionsType.TV_DEVICES]: {
        data: deviceTypesOptions[OptionsType.TV_DEVICES].mapById,
        isLoading: deviceTypesOptions[OptionsType.TV_DEVICES].loading,
        refetch: deviceTypesOptions[OptionsType.TV_DEVICES].refetch || (() => Promise.resolve()),
      },
    };
  },
  useDistributionChannelOptions: () => {
    const { mapById, loading, refetch } = useDistributionChannelOptionsFetch();

    return {
      distributionChannel: mapById,
      isDistributionChannelLoading: loading,
      refetch,
    };
  },
  useOldDistributionChannelOptions: () => {
    const { mapById, loading, refetch } = useOldDistributionChannelOptionsFetch();

    return {
      distributionChannel: mapById,
      isDistributionChannelLoading: loading,
      refetch,
    };
  },
  usePackageClone,
  usePackageCreate,
  usePackageUpdate,
  usePackageFetch,
  usePackageOperationActions,
  usePackagePermissions,
  usePackageUsedBy,
  usePackagesListFetch,
  useRelatedAccountsFetch,
  useTargetablePackagesFetch,
  useTechnologyOptionsFetch: (type, isUsed) => {
    const {
      options,
      isLoading,
      refetch = () => {
        noop();
      },
    } = useTechnologyOptionsFetch(type, isUsed);

    return {
      deviceTypeOptions: options,
      isLoading,
      refetch,
    };
  },
  useUpdateForm,
  usePackageLazyFetch,
  useDealDisableRbo,
  usePackageRetargetingDealsValidation,
};
